import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import styles from './PersonSelector.module.css';
import { useStores } from '../../../../hooks/useStore';
import { PersonModel } from '../../../../models/coursesAndPersons';
import { CreatePersonRequest } from '../../../../api/reviews/dto/CoursesAndPersonsRequest.dto';
import { Button } from '../../../../components/UI/Button/Button';
import { Modal } from '../../../../components/UI/Modal/Modal';

interface PersonSelectorProps {
  value?: string;
  onChange: (selectedPersonId: string) => void;
  placeholder?: string;
}

export const PersonSelector: React.FC<PersonSelectorProps> = observer(
  ({ value, onChange, placeholder = 'Выберите автора' }) => {
    const { userStore } = useStores();
    const [persons, setPersons] = useState<PersonModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editingPerson, setEditingPerson] = useState<PersonModel | null>(
      null,
    );
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [newPerson, setNewPerson] = useState<CreatePersonRequest>({
      first_name: '',
      last_name: '',
      age: undefined,
    });

    useEffect(() => {
      loadPersons();
    }, []);

    const loadPersons = async () => {
      setLoading(true);
      try {
        const response =
          await userStore.mainStore.apiClient.reviews.getPersons();
        if (response.success && response.body) {
          setPersons(response.body.persons);
        }
      } catch (error) {
        // Ошибка загрузки персон
      } finally {
        setLoading(false);
      }
    };

    const handleCreatePerson = async () => {
      if (!newPerson.first_name || !newPerson.last_name) {
        return;
      }

      setCreateLoading(true);
      try {
        const response =
          await userStore.mainStore.apiClient.reviews.createPerson(newPerson);
        if (response.success && response.body) {
          const createdPerson = response.body;
          setPersons((prev) => [...prev, createdPerson]);
          onChange(createdPerson.id);
          setShowCreateModal(false);
          setNewPerson({
            first_name: '',
            last_name: '',
            age: undefined,
          });
        }
      } catch (error) {
        // Ошибка создания персоны
      } finally {
        setCreateLoading(false);
      }
    };

    const handleEditPerson = async () => {
      if (
        !editingPerson ||
        !editingPerson.first_name ||
        !editingPerson.last_name
      ) {
        return;
      }

      setEditLoading(true);
      try {
        const updateData = {
          first_name: editingPerson.first_name,
          last_name: editingPerson.last_name,
          age: editingPerson.age,
          country_id: editingPerson.country_id,
          city_id: editingPerson.city_id,
        };

        const response =
          await userStore.mainStore.apiClient.reviews.updatePerson(
            editingPerson.id,
            updateData,
          );
        if (response.success && response.body) {
          const updatedPerson = response.body;
          setPersons((prev) =>
            prev.map((p) => (p.id === updatedPerson.id ? updatedPerson : p)),
          );
          setEditingPerson(null);
        }
      } catch (error) {
        // Ошибка редактирования персоны
      } finally {
        setEditLoading(false);
      }
    };

    const handleDeletePerson = async (personId: string) => {
      if (!window.confirm('Вы уверены, что хотите удалить этого автора?')) {
        return;
      }

      setDeleteLoading(true);
      try {
        const response =
          await userStore.mainStore.apiClient.reviews.deletePerson(personId);
        if (response.success) {
          setPersons((prev) => prev.filter((p) => p.id !== personId));
          if (value === personId) {
            onChange('');
          }
        }
      } catch (error) {
        // Ошибка удаления персоны
      } finally {
        setDeleteLoading(false);
      }
    };

    const startEditPerson = (person: PersonModel) => {
      setEditingPerson({ ...person });
    };

    const getPersonFullName = (person: PersonModel) => {
      return `${person.first_name} ${person.last_name}`.trim();
    };

    const selectedPerson = persons.find((p) => p.id === value);

    return (
      <div className={styles.personSelector}>
        <div className={styles.selectRow}>
          <select
            className={styles.select}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={loading}
          >
            <option value="">{loading ? 'Загрузка...' : placeholder}</option>
            {persons.map((person) => (
              <option key={person.id} value={person.id}>
                {getPersonFullName(person)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            appearance="link"
            onClick={() => setShowCreateModal(true)}
            className={styles.addButton}
            title="Добавить автора"
          >
            +
          </Button>
        </div>

        {selectedPerson && (
          <div className={styles.selectedInfo}>
            <div className={styles.personInfo}>
              <small>
                {getPersonFullName(selectedPerson)}
                {selectedPerson.age && ` (${selectedPerson.age} лет)`}
              </small>
            </div>
            <div className={styles.personActions}>
              <Button
                type="button"
                appearance="link"
                onClick={() => startEditPerson(selectedPerson)}
                className={styles.actionButton}
              >
                ✏️ Редактировать
              </Button>
              <Button
                type="button"
                appearance="link"
                onClick={() => handleDeletePerson(selectedPerson.id)}
                disabled={deleteLoading}
                className={styles.deleteButton}
              >
                {deleteLoading ? '⏳' : '🗑️'} Удалить
              </Button>
            </div>
          </div>
        )}

        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Создать нового автора"
        >
          <div className={styles.createForm}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Имя*</label>
              <input
                type="text"
                className={styles.input}
                value={newPerson.first_name}
                onChange={(e) =>
                  setNewPerson((prev) => ({
                    ...prev,
                    first_name: e.target.value,
                  }))
                }
                placeholder="Введите имя"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Фамилия*</label>
              <input
                type="text"
                className={styles.input}
                value={newPerson.last_name}
                onChange={(e) =>
                  setNewPerson((prev) => ({
                    ...prev,
                    last_name: e.target.value,
                  }))
                }
                placeholder="Введите фамилию"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Возраст</label>
              <input
                type="number"
                className={styles.input}
                value={newPerson.age || ''}
                onChange={(e) =>
                  setNewPerson((prev) => ({
                    ...prev,
                    age: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                placeholder="Введите возраст"
                min="1"
                max="120"
              />
            </div>

            <div className={styles.modalActions}>
              <Button
                type="button"
                appearance="link"
                onClick={() => setShowCreateModal(false)}
              >
                Отмена
              </Button>
              <Button
                type="button"
                appearance="auth"
                onClick={handleCreatePerson}
                disabled={
                  !newPerson.first_name || !newPerson.last_name || createLoading
                }
              >
                {createLoading ? 'Создание...' : 'Создать автора'}
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={!!editingPerson}
          onClose={() => setEditingPerson(null)}
          title="Редактировать автора"
        >
          {editingPerson && (
            <div className={styles.createForm}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Имя*</label>
                <input
                  type="text"
                  className={styles.input}
                  value={editingPerson.first_name}
                  onChange={(e) =>
                    setEditingPerson((prev) =>
                      prev
                        ? {
                            ...prev,
                            first_name: e.target.value,
                          }
                        : null,
                    )
                  }
                  placeholder="Введите имя"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Фамилия*</label>
                <input
                  type="text"
                  className={styles.input}
                  value={editingPerson.last_name}
                  onChange={(e) =>
                    setEditingPerson((prev) =>
                      prev
                        ? {
                            ...prev,
                            last_name: e.target.value,
                          }
                        : null,
                    )
                  }
                  placeholder="Введите фамилию"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Возраст</label>
                <input
                  type="number"
                  className={styles.input}
                  value={editingPerson.age || ''}
                  onChange={(e) =>
                    setEditingPerson((prev) =>
                      prev
                        ? {
                            ...prev,
                            age: e.target.value
                              ? Number(e.target.value)
                              : undefined,
                          }
                        : null,
                    )
                  }
                  placeholder="Введите возраст"
                  min="1"
                  max="120"
                />
              </div>

              <div className={styles.modalActions}>
                <Button
                  type="button"
                  appearance="link"
                  onClick={() => setEditingPerson(null)}
                >
                  Отмена
                </Button>
                <Button
                  type="button"
                  appearance="auth"
                  onClick={handleEditPerson}
                  disabled={
                    !editingPerson.first_name ||
                    !editingPerson.last_name ||
                    editLoading
                  }
                >
                  {editLoading ? 'Сохранение...' : 'Сохранить изменения'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  },
);
